import { formatDate } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import { ARABIC_LOCALE_ID, ENGLISH_LOCALE_ID } from 'src/app/@core/locale/app-locale';
import { LanguageService } from 'src/app/services/language.service';
import { LanguageEnum } from 'src/app/shared/models/item.model';

type DateInput = string | number | Date | null | undefined;

/**
 * Replaces the built-in `date` pipe in every module that imports SharedModule (after CommonModule): LOCALE_ID is
 * read once at bootstrap, so the built-in pipe keeps the old locale after the header/footer language toggle.
 * This pipe resolves the locale from the current UI language on every change detection (impure), and caches the
 * last result so unchanged inputs are not re-formatted.
 */
@Pipe({ name: 'date', pure: false })
export class LocalizedDatePipe implements PipeTransform {
  private lastKey: string | null = null;
  private lastResult: string | null = null;

  constructor(private languageService: LanguageService) {}

  transform(value: DateInput, format = 'mediumDate', timezone?: string, locale?: string): string | null {
    if (value === null || value === undefined || value === '') return null;
    const resolvedLocale = locale ?? this.currentLocale();
    const key = `${value instanceof Date ? value.getTime() : value}|${format}|${timezone ?? ''}|${resolvedLocale}`;
    if (key === this.lastKey) return this.lastResult;

    let result: string;
    try {
      result = formatDate(value, format, resolvedLocale, timezone);
    } catch {
      // Same behaviour as the built-in pipe for input it cannot format (it throws for invalid dates).
      result = formatDate(value, format, ENGLISH_LOCALE_ID, timezone);
    }
    this.lastKey = key;
    this.lastResult = result;
    return result;
  }

  private currentLocale(): string {
    return this.languageService.getCurrentLanguage() === LanguageEnum.ar ? ARABIC_LOCALE_ID : ENGLISH_LOCALE_ID;
  }
}
